import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {mapShiftEvent} from '../mappers'
import {SapStatusCode, ShiftEventDto, EventSearchResult, ShiftEvent} from '../types'

import {EventsSearchQueryKey} from './useEventsSearchApi'

export const QueryKey = 'events-events'
const INTERVAL_PERIOD_IN_MS = 30000

type UseEventParams = {
  plantId: string
  timezone: string
  eventId: string | undefined
}

type Options = Omit<UseQueryOptions<ShiftEvent | undefined, AxiosError>, 'initialData' | 'queryKey'>

export const useEvent = ({plantId, timezone, eventId}: UseEventParams, options?: Options) => {
  const queryClient = useQueryClient()
  const path = `/shifthandover/${plantId}/events/${eventId}`

  return useQuery<ShiftEvent | undefined, AxiosError>({
    queryKey: [QueryKey, eventId, plantId, timezone],
    queryFn: async ({signal}) => {
      if (eventId === undefined) return undefined
      const response = await ApiClient.axiosInstance.get<ShiftEventDto>(path, {signal})
      return mapShiftEvent(response.data, timezone)
    },
    refetchInterval: (query) =>
      query.state.data?.sapStatusCode === SapStatusCode.SUBMITTED ? INTERVAL_PERIOD_IN_MS : false,
    ...options,
    initialData: () => {
      // https://react-query.tanstack.com/guides/initial-query-data#initial-data-from-cache
      return queryClient
        .getQueryData<EventSearchResult>([EventsSearchQueryKey])
        ?.events.find((event: ShiftEvent) => event.id === eventId)
    }
  })
}

type UseEventsParams = {
  plantId: string
  eventsPerPage?: number
  pageNumber?: number
}

type UseEventsOptions = Omit<UseQueryOptions<ShiftEvent[], AxiosError>, 'queryKey'>

export const useEvents = (
  {plantId, eventsPerPage = 50, pageNumber = 0}: UseEventsParams,
  options?: UseEventsOptions
) => {
  const path = `/shifthandover/${plantId}/events`
  const params = {params: {eventsPerPage, pageNumber}}

  return useQuery<ShiftEvent[], AxiosError>({
    queryKey: [QueryKey, eventsPerPage, pageNumber, plantId],
    queryFn: async () => {
      const response = await ApiClient.axiosInstance.get<ShiftEvent[]>(path, params)
      return response.data
    },
    ...options
  })
}
